/**
 * localizeCopy — RPC contract for the AI catalog-copy localization
 * callable.
 *
 * Cross-process contract between the event and ticket-type editors (the
 * `web/src/events/` callable wrapper) and
 * `firebase/src/events/localizeCopy/localizeCopy.webhook.ts` (onCall handler,
 * wrapping `localizeCopy.chat.ts`'s one `chat()` seam). Every object on
 * both ends parses `.strict()`, each union member and each copy included
 * — an extra key fails the whole request rather than being stripped.
 *
 * An operator types one string and every buyer reads it, whatever
 * language they arrived in. This callable is what gets that string to
 * every locale `@momo/i18n`'s `LocaleEnum` carries, as a draft the
 * operator reviews and accepts themselves — nothing it returns is
 * stored by this callable, and the accepting write is the editor's own
 * existing save.
 *
 * ONE CALL PER EVENT, NOT PER SUBJECT. `subjects` is an array because
 * an event and its whole ticket catalog are localized in one pass: the
 * description and every type's label reach the model together and come back
 * in one voice, and a catalog of a dozen types costs one call against
 * the deployment-wide hourly window rather than a dozen.
 *
 * `key` is the caller's own correlation id — the event id or the type
 * id — echoed back untouched so the editor can land each result on the
 * draft it came from. The handler never interprets it, and nothing
 * downstream resolves a document by it.
 *
 * Fields justified:
 *   subjects       — the texts to localize, each tagged with the kind of
 *                     thing it names, which is what selects the register
 *                     the copy is written in.
 *   targetLocales  — every locale the copy must exist in, source
 *                     language included — data, not a hardcoded locale
 *                     pair, so a third locale is additive here too. A set:
 *                     each locale appears once, since a second mention names
 *                     no second draft.
 *
 * Output:
 *   detectedLocale — the language the operator typed in, surfaced back
 *                     so the editor can tell them which returned pane is
 *                     their own words restated and which is a
 *                     translation. Declared before `subjects` because
 *                     the model reads the source's language before it
 *                     writes a word in either.
 *   subjects       — one result per requested subject, correlated by the
 *                     same `key` the request carried.
 *
 * Excluded:
 *   an event name  — the event's own name is a proper noun on posters,
 *     wallet passes and invoices; a translated event name is a different
 *     event, and there is no field on `Event` to store one. The
 *     event/ticketType discriminant is what keeps that unrepresentable
 *     rather than merely undocumented.
 *   a per-subject detected locale — one operator fills one editor in one
 *     sitting; a catalog whose types were typed in different languages
 *     is a mixed catalog the operator can see for themselves, and a
 *     per-subject answer would invite the model to hedge subject by
 *     subject rather than read the whole submission once.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { LocaleEnum } from "@momo/i18n";
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

/**
 * Discriminated because the kind decides which texts exist, not merely how
 * they read: an event carries a description and no localizable name — its own name
 * is a proper noun — while a ticket type carries a label the buyer scans a
 * list by and a description under it. A flat object with an optional `name`
 * leaves an event with a translated name representable, which is the one
 * thing this contract exists to refuse.
 * Both literals carry a `.describe()` naming their own token, the same
 * reason `FormatLegalDocument`'s block union does — a model asked for a
 * ticket type with the token unnamed answers `"ticket_type"` and fails
 * the union post-hoc.
 */
const LocalizeCopySubject = z.discriminatedUnion("kind", [
	z
		.object({
			kind: z
				.literal("event")
				.describe(
					'an event and its storefront description: the exact word "event"',
				),
			key: z
				.string()
				.min(1)
				.describe("the caller's correlation id for this subject"),
			description: z
				.string()
				.min(1)
				.describe(
					"the event's storefront description as the operator typed it",
				),
		})
		.strict(),
	z
		.object({
			kind: z
				.literal("ticketType")
				.describe('a sellable ticket type: the exact word "ticketType"'),
			key: z
				.string()
				.min(1)
				.describe("the caller's correlation id for this subject"),
			name: z
				.string()
				.min(1)
				.describe(
					"the ticket type's label as the operator typed it — a short label, never a sentence",
				),
			description: z
				.string()
				.min(1)
				.nullish()
				.describe(
					"the ticket type's short buyer-facing copy as the operator typed it, absent when the type carries none",
				),
		})
		.strict(),
]);

export const LocalizeCopyInput = z
	.object({
		orgId: Id,
		subjects: z
			.array(LocalizeCopySubject)
			.min(1)
			.describe(
				"every text to localize in this pass — an event, its ticket types, or any subset of them",
			),
		targetLocales: z
			.array(LocaleEnum)
			.min(1)
			.describe("every locale the copy must exist in, source language included")
			// A set spelled as an array: one locale asked for twice is one locale,
			// and the draft that answers it once is a complete draft. Refused here,
			// where the caller is told what it sent, rather than downstream where a
			// repeat reads as a subject the model left short a language — a verdict
			// reached only after the call and the hourly slot are already spent.
			.refine(
				(locales) => new Set(locales).size === locales.length,
				"a locale is asked for once or not at all",
			),
	})
	.strict();
export type LocalizeCopyInput = z.infer<typeof LocalizeCopyInput>;

/**
 * One subject the model has written in every asked-for language — the
 * counterpart of `LocalizeCopySubject`, which is the same subject on the way
 * in. It mirrors the request's own discriminant so each locale's texts carry
 * the fields their subject can actually store. The caller lands one of these
 * by `key`, against the subject it sent under that key.
 *
 * Each member calls `.describe()` on `LocaleEnum` for itself rather than
 * sharing one built instance between them. The JSON Schema Genkit emits
 * inlines a zod instance's first occurrence and emits a `$ref` back to that
 * path for every later one, and a `$ref` reached from a required property is
 * the one construct Google's structured-output contract admits only
 * alongside `$defs`/`$anchor`, which nothing here declares.
 */
const LocalizedSubject = z.discriminatedUnion("kind", [
	z
		.object({
			kind: z
				.literal("event")
				.describe(
					'an event and its storefront description: the exact word "event"',
				),
			key: z
				.string()
				.min(1)
				.describe("the correlation id the request carried for this subject"),
			copies: z
				.array(
					z
						.object({
							locale: LocaleEnum.describe("the locale this copy is written in"),
							description: z
								.string()
								.min(1)
								.describe("the event's storefront description in this locale"),
						})
						.strict(),
				)
				.min(1)
				.describe("one storefront description per requested locale"),
		})
		.strict(),
	z
		.object({
			kind: z
				.literal("ticketType")
				.describe('a sellable ticket type: the exact word "ticketType"'),
			key: z
				.string()
				.min(1)
				.describe("the correlation id the request carried for this subject"),
			copies: z
				.array(
					z
						.object({
							locale: LocaleEnum.describe("the locale this copy is written in"),
							name: z
								.string()
								.min(1)
								.describe(
									"the ticket type's label in this locale — a short label, never a sentence",
								),
							description: z
								.string()
								.min(1)
								.nullish()
								.describe(
									"the ticket type's short buyer-facing copy in this locale, absent when and only when the subject carried none",
								),
						})
						.strict(),
				)
				.min(1)
				.describe("one label and copy pair per requested locale"),
		})
		.strict(),
]);
type LocalizedSubject = z.infer<typeof LocalizedSubject>;

export const LocalizeCopyOutput = z
	.object({
		detectedLocale: LocaleEnum.describe(
			"the language the submitted copy is written in",
		),
		subjects: z
			.array(LocalizedSubject)
			.min(1)
			.describe("one result per requested subject, correlated by key"),
	})
	.strict()
	// Totality, to the limit of what an output alone can see. An output schema
	// never receives the input, so "every locale the caller asked for is here"
	// and "every subject the caller sent came back" are not expressible — those
	// are the handler's, checked against the request it still holds. What is
	// expressible is internal coherence, and it is the staleness class this
	// design exists to prevent: a review the operator accepts whose panes are
	// silently short one language stores a map that reads as complete.
	.superRefine((output, ctx) => {
		const [first] = output.subjects;
		if (!first) return;
		// One entry per copy, repeats kept: a locale answered twice is the same
		// staleness by another route, since the map the client builds keeps
		// whichever of the two it reads last and drops the other in silence.
		const localesOf = (subject: LocalizedSubject) =>
			subject.copies.map((copy) => copy.locale).sort();
		const reference = [...new Set(localesOf(first))].join(", ");
		output.subjects.forEach((subject, index) => {
			const locales = localesOf(subject);
			const covered = [...new Set(locales)];
			const repeated = [
				...new Set(
					locales.filter((locale, at) => locales.indexOf(locale) !== at),
				),
			];
			if (repeated.length)
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["subjects", index, "copies"],
					message: `subject ${subject.key} carries more than one ${repeated.join(", ")} copy — a locale is answered once or not at all, and a second answer displaces the first where the map is built`,
				});
			if (!covered.includes(output.detectedLocale))
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["subjects", index, "copies"],
					message: `subject ${subject.key} carries no ${output.detectedLocale} copy, the language the source itself was read as`,
				});
			if (covered.join(", ") !== reference)
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["subjects", index, "copies"],
					message: `subject ${subject.key} is localized into ${covered.join(", ")} while ${first.key} is localized into ${reference} — a subject whose languages differ from its siblings'`,
				});
		});
	});
export type LocalizeCopyOutput = z.infer<typeof LocalizeCopyOutput>;
